import { PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import { PageRemoveIcon, TextDocumentIcon } from "@fluentui/react-icons-mdl2";
import { useState } from "react";

import {
  Invoice,
  Tariff,
  InvoiceStatusEnum,
  PaginationInfo
} from "../../interfaces/invoices-response";
import InvoiceCard from "../invoiceCard/invoice-card";
import PaymentModal from "../paymentModal/paymentModal";

import {
  Content,
  EmptyInvoiceList,
  InvoiceList,
  InvoicesSection,
  SectionTitle,
  SidebarCard
} from "./style";

import { ModalListItens } from "@/components/ModalSelect";
import { useApi, ExecuteData } from "@/core/libs/api/react-query/useApi";
import { parseDateToMonthYear } from "@/utils/FormatDate";

interface SidebarProps {
  invoices: Invoice[];
  expandedInvoices: number[];
  selectedTariff?: Tariff;
  onToggleInvoice: (invoiceId: number) => void;
  setSelectedTariff: (tariff: Tariff) => void;
  isLoading?: boolean;
  document: string;
  isGeneratePaymentEnabled: boolean;
}

export default function Sidebar({
  invoices,
  expandedInvoices,
  selectedTariff,
  onToggleInvoice,
  setSelectedTariff,
  isLoading = false,
  document,
  isGeneratePaymentEnabled: enableGeneratePayment
}: SidebarProps) {
  const { execute } = useApi();
  const [invoiceTariffs, setInvoiceTariffs] = useState<
    Record<number, Tariff[]>
  >({});
  const [loadingTariffIds, setLoadingTariffIds] = useState<number[]>([]);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<number>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGeneratingPayment, setIsGeneratingPayment] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<number[]>([]);
  const [pagination, setPagination] = useState<Record<number, PaginationInfo>>(
    {}
  );
  const PAGE_SIZE = 30;

  const fetchInvoiceTariffs = async (invoiceId: number, page = 1) => {
    setLoadingTariffIds(prev => [...prev, invoiceId]);
    setCurrentInvoiceId(invoiceId);

    try {
      const data: ExecuteData = {
        params: { document, page, take: PAGE_SIZE },
        url: `invoices/${invoiceId}/tariffs`,
        messages: {
          error: "Tivemos um problema ao carregar as tarifas da fatura"
        }
      };

      const response = await execute(data);

      if (response && response.data) {
        const { tariffs: newTariffs, pagination: paginationData } =
          response.data;
        const hasMore = paginationData?.hasMore ?? false;

        setInvoiceTariffs(prev => ({
          ...prev,
          [invoiceId]: newTariffs
        }));

        setPagination(prev => ({
          ...prev,
          [invoiceId]: {
            page,
            hasMore,
            totalPages: paginationData?.totalPages || 0,
            totalCount: paginationData?.totalCount || 0
          }
        }));
      }
      setLoadingTariffIds(prev => prev.filter(id => id !== invoiceId));
    } catch (error) {
      setLoadingTariffIds(prev => prev.filter(id => id !== invoiceId));
      throw new Error(error);
    }
  };

  const handleLoadMoreTariffs = (invoiceId: number) => {
    const currentPage = pagination[invoiceId]?.page || 0;
    const nextPage = currentPage + 1;
    fetchInvoiceTariffs(invoiceId, nextPage);
  };

  const handleLoadPreviousTariffs = (invoiceId: number) => {
    const currentPage = pagination[invoiceId]?.page || 2;

    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      fetchInvoiceTariffs(invoiceId, prevPage);
    }
  };

  const handleToggleInvoice = (invoiceId: number) => {
    const isExpanding = !expandedInvoices.includes(invoiceId);

    if (isExpanding) {
      if (!invoiceTariffs[invoiceId]) {
        fetchInvoiceTariffs(invoiceId, 1);
      }
      setCurrentInvoiceId(invoiceId);
    } else {
      setCurrentInvoiceId(null);
    }

    onToggleInvoice(invoiceId);
  };

  const handleModalAction = (selectedIds: number[]) => {
    if (selectedIds.length === 0) {
      setIsModalOpen(false);

      return;
    }

    setIsGeneratingPayment(true);

    setSelectedInvoiceIds(selectedIds);
    setIsPaymentModalOpen(true);

    setIsGeneratingPayment(false);
    setIsModalOpen(false);
  };

  const handleOpenPaymentModal = () => {
    setIsModalOpen(true);
  };

  const getInvoiceText = (invoice: Invoice) => {
    const { month, year } = parseDateToMonthYear(invoice?.dueDate);

    return `Fatura ${invoice.id} - Venc: ${month}/${year}`;
  };

  const pendingPaymentInvoices = invoices
    .filter(invoice => invoice.status === InvoiceStatusEnum.OPEN)
    .map(invoice => ({
      id: invoice.id,
      text: getInvoiceText(invoice),
      required: invoice.isPaymentRequired === true
    }));

  return (
    <SidebarCard>
      <Content>
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "2rem"
            }}
          >
            <Spinner size={SpinnerSize.large} label="Carregando..." />
          </div>
        ) : (
          <>
            <InvoicesSection>
              <SectionTitle>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <TextDocumentIcon />
                  Faturas
                </div>
                {invoices.length > 0 && (
                  <PrimaryButton
                    style={{ padding: "6px", fontSize: "12px" }}
                    onClick={handleOpenPaymentModal}
                    disabled={!enableGeneratePayment}
                  >
                    Gerar pagamento
                  </PrimaryButton>
                )}
              </SectionTitle>

              {invoices.length > 0 ? (
                <InvoiceList>
                  {invoices.map(invoice => (
                    <InvoiceCard
                      key={invoice.id}
                      invoice={invoice}
                      expanded={expandedInvoices.includes(invoice.id)}
                      selectedTariff={selectedTariff}
                      onToggle={handleToggleInvoice}
                      setSelectedTariff={setSelectedTariff}
                      customTariffs={invoiceTariffs[invoice.id] || []}
                      isLoadingTariffs={loadingTariffIds.includes(invoice.id)}
                      currentInvoiceId={currentInvoiceId}
                      onLoadMoreTariffs={handleLoadMoreTariffs}
                      onLoadPreviousTariffs={handleLoadPreviousTariffs}
                      pagination={pagination}
                      document={document}
                    />
                  ))}
                </InvoiceList>
              ) : (
                <EmptyInvoiceList>
                  <PageRemoveIcon
                    style={{
                      width: "30px",
                      height: "30px",
                      marginBottom: "10px"
                    }}
                  />
                  <p>Nenhuma fatura encontrada.</p>
                </EmptyInvoiceList>
              )}
            </InvoicesSection>
          </>
        )}
      </Content>

      <ModalListItens
        title="Selecione as faturas para pagamento"
        items={pendingPaymentInvoices}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onAction={handleModalAction}
        actionButtonText="Link de pagamento"
        loadingText="Gerando pix..."
        isLoading={isGeneratingPayment}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onDismiss={() => setIsPaymentModalOpen(false)}
        invoiceIds={selectedInvoiceIds}
        document={document}
      />
    </SidebarCard>
  );
}
